import { createContext, useCallback, useContext, useMemo } from 'react'
import { useInterpret } from '@xstate/react'
import { contentMachine } from 'helpCenter/machines/contentMachine'
import { getCategory, GetCategoryProps } from 'helpCenter/api/getCategory'
import { getCategories, GetCategoriesProps } from 'helpCenter/api/getCategories'
import { getArticle, GetArticleProps } from 'helpCenter/api/getArticle'
import {
  getArticlesInCategory,
  GetArticlesInCategoryProps,
} from 'helpCenter/api/getArticles'
import { GetSearchProps, search } from 'helpCenter/api/search'
import {
  Article,
  ArticleRef,
  Category,
  SearchArticleRef,
} from 'helpCenter/types'
import { useHistory } from 'react-router-dom'

export type XStateService = {
  send: any
  subscribe: any
}

export type CategoryActions = {
  get: (input: GetCategoryProps) => void
  list: (input: GetCategoriesProps) => void
}
export type ArticleActions = {
  get: (input: GetArticleProps) => void
  list: (input: GetArticlesInCategoryProps) => void
  search: (input: GetSearchProps) => void
}
const helpCenterContext = createContext(
  {} as {
    categories: {
      service: XStateService
      actions: CategoryActions
    }
    articles: {
      service: XStateService
      actions: ArticleActions
    }
    search: {
      service: XStateService
    }
  }
)

export const HelpCenterProvider = (props: { children: JSX.Element }) => {
  // const addError = useGlobalError().addError
  const history = useHistory()
  const goToErrorPage = useCallback(
    (message?: string) => {
      const previousState = history.location.state as { errorOrigin?: string }
      const currentEndpoint = previousState?.errorOrigin
        ? previousState.errorOrigin
        : history.location.pathname + history.location.search
      history.push('/help/error', {
        errorMessage: message,
        errorOrigin: currentEndpoint,
      })
    },
    [history]
  )
  const articlesService = useInterpret(contentMachine)
  const categoriesService = useInterpret(contentMachine)
  const searchService = useInterpret(contentMachine)

  const { articleActions, categoryActions } = useMemo(() => {
    const categoryActions = {
      get(input: GetCategoryProps) {
        getCategory(input)
          .then((category: Category) => {
            categoriesService.send('LOAD', { data: [category] })
          })
          .catch((error: any) => {
            goToErrorPage(error.message)
          })
      },
      list(input: GetCategoriesProps) {
        getCategories(input)
          .then((categories: Category[]) => {
            categoriesService.send('LOAD', { data: categories })
          })
          .catch((error: any) => {
            goToErrorPage(error.message)
          })
      },
    }
    const articleActions = {
      get(input: GetArticleProps) {
        articlesService.send('LOAD', {
          data: [{ id: input.articleId, __requestStatus: 'requested' }],
        })
        getArticle(input)
          .then((article: Article) => {
            articlesService.send('LOAD', {
              data: [{ ...article, __requestStatus: 'done' }],
            })
          })
          .catch((error: any) => {
            articlesService.send('LOAD', {
              data: [{ id: input.articleId, __requestStatus: 'failed' }],
            })
            goToErrorPage(error.message)
          })
      },
      list(input: GetArticlesInCategoryProps) {
        categoriesService.send('LOAD', {
          data: [
            {
              id: input.categoryId,
              articles: {
                __requestStatus: 'requested',
              },
            },
          ],
        })
        getArticlesInCategory(input)
          .then((articles: ArticleRef[]) => {
            articlesService.send('LOAD', { data: articles })
            categoriesService.send('LOAD', {
              data: [
                {
                  id: input.categoryId,
                  articles: {
                    ids: articles.map(({ id }: { id: string }) => id),
                    params: input.params,
                    __requestStatus: 'done',
                  },
                },
              ],
            })
          })
          .catch((error: any) => {
            categoriesService.send('LOAD', {
              data: [
                {
                  id: input.categoryId,
                  articles: {
                    __requestStatus: 'failed',
                  },
                },
              ],
            })
            goToErrorPage(error.message)
          })
      },
      search(input: GetSearchProps) {
        search(input)
          .then((articles: SearchArticleRef[]) => {
            articlesService.send('LOAD', { data: articles })
            searchService.send('LOAD', {
              data: [
                {
                  id: input.query,
                  articleIds: articles.map(({ id }: { id: string }) => id),
                },
              ],
            })
          })
          .catch((error: any) => {
            goToErrorPage(error.message)
          })
      },
    }
    return { articleActions, categoryActions }
  }, [categoriesService, articlesService, searchService, goToErrorPage])

  return (
    <helpCenterContext.Provider
      value={{
        categories: {
          service: categoriesService,
          actions: categoryActions,
        },
        articles: {
          service: articlesService,
          actions: articleActions,
        },
        search: {
          service: searchService,
        },
      }}>
      {props.children}
    </helpCenterContext.Provider>
  )
}

export const useHelpCenter = () => {
  return useContext(helpCenterContext)
}
